import type { Ref } from 'react';
import { PlugIcon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useConnections } from '../hooks/useConnections';
import type { ConnectionProviderType } from '../types';

export interface ConnectionSelectProps {
  workspaceId: string;
  /** Only enabled connections of this exact provider are offered
   *  (research.md D5) — a disabled or mismatched-provider connection would
   *  fail server-side with `MismatchedConnectionTypeError`/a disabled-state
   *  refusal anyway; filtering here keeps the person from picking one only
   *  to be told no after submitting. */
  providerType: ConnectionProviderType;
  value: string | undefined;
  onChange: (connectionId: string) => void;
  /**
   * Offered as the field's action when no eligible connection exists, and as
   * the list's last item when some do (spec 124 US1). Without it the picker
   * behaves as it always has.
   */
  onCreateRequest?: () => void;
  disabled?: boolean;
  'aria-label'?: string;
  /** The focusable element — trigger, or the action when there is none. */
  ref?: Ref<HTMLButtonElement>;
}

/** The list item that is an action; never reaches `onChange`. */
export const CREATE_CONNECTION_ITEM = '__create_connection__';

const PROVIDER_LABEL: Record<ConnectionProviderType, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  jira: 'Jira',
  oci_registry: 'registry',
};

/**
 * Pick one enabled, provider-matching connection in this workspace.
 *
 * The one export from this feature package meant for a scheduled trigger
 * profile's form to reach for (contracts/connections-feature-interface.md)
 * — mirrors `WorkflowSelect`'s shape (a plain `<Select>` wrapping this
 * feature's own list hook) so a caller's field wiring reads consistently
 * with the workflow/step pickers already beside it.
 */
export function ConnectionSelect({
  workspaceId,
  providerType,
  value,
  onChange,
  onCreateRequest,
  disabled,
  ref,
  'aria-label': ariaLabel,
}: ConnectionSelectProps) {
  const { connections, isPending, loadFailed } = useConnections(workspaceId);
  const eligible = (connections ?? []).filter(
    (connection) => connection.enabled && connection.type === providerType,
  );

  const placeholder = isPending
    ? 'Loading connections…'
    : loadFailed
      ? 'Connections could not be loaded'
      : eligible.length === 0
        ? 'No enabled connections for this provider'
        : 'Select a connection';

  const label = ariaLabel ?? 'Connection';
  const provider = PROVIDER_LABEL[providerType];

  // Nothing to pick: the field is the way on (spec 124 FR-001), drawn with the
  // `Input` border so it sits where the select would. Not while an id is
  // already chosen — after a create, the list is re-read a moment later, and
  // the field must not flash back to the action.
  if (
    onCreateRequest &&
    !isPending &&
    !loadFailed &&
    eligible.length === 0 &&
    !value
  ) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onCreateRequest}
        disabled={disabled}
        aria-label={`${label}: no ${provider} connection yet, create one`}
        className="border-input text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring focus-visible:border-ring flex h-8 w-full items-center gap-2 rounded-lg border bg-transparent px-2.5 text-left text-sm transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:opacity-50"
      >
        <PlugIcon
          aria-hidden="true"
          className="size-4 shrink-0"
          strokeWidth={1.5}
        />
        <span className="min-w-0 truncate">
          No {provider} connection yet — Create one
        </span>
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Select
        value={value}
        onValueChange={(next) => {
          if (next === CREATE_CONNECTION_ITEM) {
            onCreateRequest?.();
            return;
          }
          if (next) onChange(next);
        }}
        disabled={
          disabled ||
          isPending ||
          loadFailed ||
          (eligible.length === 0 && !value)
        }
      >
        <SelectTrigger ref={ref} aria-label={label}>
          {/* Same reasoning `WorkflowSelect` documents: a children function on
            `SelectValue` always wins over its `placeholder` prop, so the
            empty case is handled here rather than left to `placeholder`. */}
          <SelectValue>
            {(id: string | undefined) =>
              id
                ? (eligible.find((connection) => connection.id === id)?.name ??
                  id)
                : placeholder
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {eligible.map((connection) => (
            <SelectItem key={connection.id} value={connection.id}>
              {connection.name}
            </SelectItem>
          ))}
          {onCreateRequest ? (
            <SelectItem
              value={CREATE_CONNECTION_ITEM}
              className="border-border mt-1 border-t pt-1.5"
            >
              <span className="flex items-center gap-2">
                <PlusIcon
                  aria-hidden="true"
                  className="text-muted-foreground size-4 shrink-0"
                  strokeWidth={1.5}
                />
                Create new connection…
              </span>
            </SelectItem>
          ) : null}
        </SelectContent>
      </Select>
      {onCreateRequest && loadFailed ? (
        // Listing and creating are separate requests; a failed read must not
        // close the only door.
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto self-start px-0"
          onClick={onCreateRequest}
          disabled={disabled}
        >
          <PlusIcon aria-hidden="true" />
          Create a connection
        </Button>
      ) : null}
    </div>
  );
}
